import React, { useEffect, useMemo, useState } from "react";
import { api, setStoreIdHeader, setUserHeader } from "./api";

const PERMS = [
  { code: "business.manage", label: "Business / Store Manage" },

  { code: "users.view", label: "View Users" },
  { code: "users.manage", label: "Manage Users" },

  { code: "inventory.view", label: "View Inventory" },
  { code: "inventory.manage", label: "Manage Inventory" },

  { code: "sales.create", label: "Create Sales" },
  { code: "reports.view", label: "View Reports" },
  { code: "pos.sell", label: "POS Sell" },

  { code: "hotel.view", label: "Hotel Dashboard" },
  { code: "hotel.rooms", label: "Manage Rooms" },
  { code: "hotel.guests", label: "Manage Guests" },
  { code: "hotel.checkin", label: "Check-In / Check-Out" },
  { code: "hotel.stays", label: "Manage Stays" },
  { code: "hotel.supplies", label: "Hotel Supplies" },
  { code: "hotel.shifts", label: "Hotel Shifts" },
  { code: "hotel.reports", label: "Hotel Reports" },
  { code: "hotel.manage", label: "Hotel Administration" },
];

function uniq(arr) {
  const s = new Set();
  const out = [];

  (arr || []).forEach((x) => {
    const v = String(x || "").trim();
    if (v && !s.has(v)) {
      s.add(v);
      out.push(v);
    }
  });

  return out;
}

function hasStar(perms) {
  return Array.isArray(perms) && perms.includes("*");
}

const S = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 12,
    background: "#fff",
  },
  row: {
    display: "grid",
    gap: 10,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
    gap: 10,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
    gap: 10,
  },
  label: {
    fontSize: 12,
    opacity: 0.75,
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: 9,
    borderRadius: 10,
    border: "1px solid #d1d5db",
  },
  btn: {
    padding: "9px 11px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    cursor: "pointer",
  },
  btnP: {
    padding: "9px 11px",
    borderRadius: 10,
    border: "1px solid #111827",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
  },
  tag: {
    fontSize: 12,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
};

export default function UsersManager({ onBack }) {
  const storeId = useMemo(() => Number(localStorage.getItem("store_id") || 0), []);
  const username = useMemo(() => String(localStorage.getItem("username") || ""), []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [active, setActive] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [msg, setMsg] = useState("");

  const toast = (m) => {
    setMsg(m);
    clearTimeout(toast._t);
    toast._t = setTimeout(() => setMsg(""), 3500);
  };

  const syncHeaders = () => {
    setStoreIdHeader(storeId);
    setUserHeader(username);
  };

  const load = async (p = 1) => {
    try {
      setLoading(true);
      setMsg("");

      if (!storeId) {
        toast("Missing store_id. Login again.");
        setRows([]);
        return;
      }

      if (!username) {
        toast("Missing username. Login again.");
        setRows([]);
        return;
      }

      syncHeaders();

      const res = await api.get("/server.php/users", {
        params: {
          q: q || "",
          active: active || "all",
          page: p,
          limit,
        },
      });

      if (!res.data?.success) {
        toast(res.data?.message || "Failed to load users");
        setRows([]);
        return;
      }

      setRows(Array.isArray(res.data.data) ? res.data.data : []);
      setPage(Number(res.data.page || p));
    } catch (e) {
      toast(e?.response?.data?.message || e.message || "Load error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [newUser, setNewUser] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newRole, setNewRole] = useState("cashier");
  const [newActive, setNewActive] = useState(true);
  const [newPerms, setNewPerms] = useState([]);

  const togglePerm = (list, setter, code) => {
    if (list.includes(code)) {
      setter(list.filter((x) => x !== code));
    } else {
      setter([...list, code]);
    }
  };

  const create = async (e) => {
    e.preventDefault();

    try {
      if (!newUser.trim()) return toast("Username is required");
      if ((newPass || "").length < 6) return toast("Password must be at least 6 characters");

      syncHeaders();

      const res = await api.post("/server.php/users", {
        store_id: storeId,
        full_name: fullName,
        email,
        phone,
        username: newUser,
        password: newPass,
        role: newRole,
        is_active: newActive ? 1 : 0,
        permissions: uniq(newPerms),
      });

      if (!res.data?.success) {
        toast(res.data?.message || "Create failed");
        return;
      }

      toast("User created");

      setFullName("");
      setEmail("");
      setPhone("");
      setNewUser("");
      setNewPass("");
      setNewRole("cashier");
      setNewActive(true);
      setNewPerms([]);

      load(1);
    } catch (e2) {
      toast(e2?.response?.data?.message || e2.message || "Create error");
    }
  };

  const setActiveFlag = async (u, enabled) => {
    try {
      syncHeaders();

      const res = await api.put(`/server.php/users/${u.id}/active`, {
        store_id: storeId,
        is_active: enabled ? 1 : 0,
      });

      if (!res.data?.success) {
        toast(res.data?.message || "Update failed");
        return;
      }

      setRows((prev) =>
        prev.map((r) => (r.id === u.id ? { ...r, is_active: enabled ? 1 : 0 } : r))
      );
    } catch (e) {
      toast(e?.response?.data?.message || e.message || "Update error");
    }
  };

  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const savePassword = async () => {
    if (!pwUser) return;
    if ((pw || "").length < 6) return toast("Password must be at least 6 characters");

    try {
      setPwSaving(true);
      syncHeaders();

      const res = await api.put(`/server.php/users/${pwUser.id}/password`, {
        store_id: storeId,
        password: pw,
      });

      if (!res.data?.success) {
        toast(res.data?.message || "Password update failed");
        return;
      }

      toast("Password updated");
      setPwUser(null);
      setPw("");
    } catch (e) {
      toast(e?.response?.data?.message || e.message || "Password update error");
    } finally {
      setPwSaving(false);
    }
  };

  const [permUser, setPermUser] = useState(null);
  const [permList, setPermList] = useState([]);
  const [permSaving, setPermSaving] = useState(false);

  const openPerms = (u) => {
    setPermUser(u);
    setPermList(uniq(Array.isArray(u.permissions) ? u.permissions : []));
  };

  const savePerms = async () => {
    if (!permUser) return;

    try {
      setPermSaving(true);
      syncHeaders();

      const cleaned = uniq(permList);

      const res = await api.put(`/server.php/users/${permUser.id}/permissions`, {
        store_id: storeId,
        permissions: cleaned,
      });

      if (!res.data?.success) {
        toast(res.data?.message || "Save permissions failed");
        return;
      }

      toast("Permissions saved");

      setRows((prev) =>
        prev.map((r) => (r.id === permUser.id ? { ...r, permissions: cleaned } : r))
      );

      setPermUser((prev) => (prev ? { ...prev, permissions: cleaned } : prev));
    } catch (e) {
      toast(e?.response?.data?.message || e.message || "Save error");
    } finally {
      setPermSaving(false);
    }
  };

  const fmtPerms = (p) => {
    if (hasStar(p)) return "ALL (*)";
    const list = Array.isArray(p) ? p : [];
    return list.length ? list.join(", ") : "—";
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {onBack && (
          <button style={S.btn} onClick={onBack}>
            Back
          </button>
        )}

        <h2 style={{ margin: 0 }}>User Management</h2>

        <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.75 }}>
          Store <b>#{storeId || "—"}</b> · User <b>{username || "—"}</b>
        </div>
      </div>

      {msg && (
        <div style={{ ...S.card, background: "#fff7ed", borderColor: "#fed7aa" }}>
          {msg}
        </div>
      )}

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>Create User</h3>

        <form onSubmit={create} style={S.row}>
          <div style={S.grid3}>
            <div>
              <div style={S.label}>Full name</div>
              <input style={S.input} value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>

            <div>
              <div style={S.label}>Email</div>
              <input style={S.input} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div>
              <div style={S.label}>Phone</div>
              <input style={S.input} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          <div style={S.grid3}>
            <div>
              <div style={S.label}>Username</div>
              <input style={S.input} value={newUser} onChange={(e) => setNewUser(e.target.value)} />
            </div>

            <div>
              <div style={S.label}>Password (min 6)</div>
              <input
                style={S.input}
                type="password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
              />
            </div>

            <div>
              <div style={S.label}>Role</div>
              <select style={S.input} value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                <option value="admin">admin</option>
                <option value="manager">manager</option>
                <option value="cashier">cashier</option>
                <option value="receptionist">receptionist</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
              Active
            </label>

            <button type="button" style={S.btn} onClick={() => setNewPerms(PERMS.map((p) => p.code))}>
              Select all perms
            </button>

            <button type="button" style={S.btn} onClick={() => setNewPerms([])}>
              Clear perms
            </button>

            <button type="button" style={S.btn} onClick={() => setNewPerms([
              "hotel.view",
              "hotel.rooms",
              "hotel.guests",
              "hotel.checkin",
              "hotel.stays",
            ])}>
              Reception perms
            </button>

            <button type="button" style={S.btn} onClick={() => setNewPerms([
              "hotel.view",
              "hotel.rooms",
              "hotel.guests",
              "hotel.checkin",
              "hotel.stays",
              "hotel.supplies",
              "hotel.shifts",
              "hotel.reports",
            ])}>
              Hotel manager perms
            </button>

            <button type="submit" style={{ ...S.btnP, marginLeft: "auto" }}>
              Create User
            </button>
          </div>

          <div>
            <div style={S.label}>Permissions (checkboxes)</div>

            <div style={S.grid3}>
              {PERMS.map((p) => (
                <label key={p.code} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={newPerms.includes(p.code)}
                    onChange={() => togglePerm(newPerms, setNewPerms, p.code)}
                  />
                  <span>{p.label}</span>
                  <span style={{ marginLeft: "auto", opacity: 0.65, fontSize: 12 }}>
                    {p.code}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </form>
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Users</h3>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
            <input
              style={{ ...S.input, width: 260 }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name/username/email/phone"
            />

            <select style={{ ...S.input, width: 130 }} value={active} onChange={(e) => setActive(e.target.value)}>
              <option value="all">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            <select style={{ ...S.input, width: 110 }} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>

            <button
              style={S.btnP}
              onClick={() => {
                setPage(1);
                load(1);
              }}
            >
              {loading ? "Loading..." : "Search"}
            </button>

            <button style={S.btn} onClick={() => load(page)}>
              Reload
            </button>
          </div>
        </div>

        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table width="100%" cellPadding="10" style={{ borderCollapse: "collapse" }}>
            <thead style={{ background: "#f3f4f6" }}>
              <tr>
                <th align="left">ID</th>
                <th align="left">Full name</th>
                <th align="left">Username</th>
                <th align="left">Role</th>
                <th align="left">Active</th>
                <th align="left">Permissions</th>
                <th align="left">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7">Loading…</td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="7">
                    No users found. Confirm the request sends <b>X-Store-ID</b> and <b>X-User</b>.
                  </td>
                </tr>
              )}

              {rows.map((u) => (
                <tr key={u.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td>{u.id}</td>
                  <td>{u.full_name || "—"}</td>
                  <td>{u.username}</td>
                  <td>
                    <span style={S.tag}>{u.role || "cashier"}</span>
                  </td>
                  <td>
                    <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={Number(u.is_active) === 1}
                        onChange={(e) => setActiveFlag(u, e.target.checked)}
                      />
                      {Number(u.is_active) === 1 ? "Yes" : "No"}
                    </label>
                  </td>
                  <td style={{ maxWidth: 450, fontSize: 12, opacity: 0.9 }}>
                    {fmtPerms(u.permissions)}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button style={S.btn} onClick={() => openPerms(u)}>
                        Permissions
                      </button>

                      <button
                        style={S.btn}
                        onClick={() => {
                          setPwUser(u);
                          setPw("");
                        }}
                      >
                        Reset Password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
          <button
            style={S.btn}
            disabled={page <= 1 || loading}
            onClick={() => {
              const p = Math.max(1, page - 1);
              setPage(p);
              load(p);
            }}
          >
            Prev
          </button>

          <div style={{ fontSize: 12, opacity: 0.75 }}>Page: {page}</div>

          <button
            style={S.btn}
            disabled={loading}
            onClick={() => {
              const p = page + 1;
              setPage(p);
              load(p);
            }}
          >
            Next
          </button>
        </div>
      </div>

      {permUser && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0 }}>
              Permissions: {permUser.full_name || permUser.username} (#{permUser.id})
            </h3>

            <button style={{ ...S.btn, marginLeft: "auto" }} onClick={() => setPermUser(null)}>
              Close
            </button>
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={S.label}>Set exact permissions</div>

            <div style={S.grid2}>
              {PERMS.map((p) => (
                <label key={p.code} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={permList.includes(p.code)}
                    onChange={() => togglePerm(permList, setPermList, p.code)}
                  />
                  <span>{p.label}</span>
                  <span style={{ marginLeft: "auto", opacity: 0.65, fontSize: 12 }}>
                    {p.code}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button style={S.btnP} disabled={permSaving} onClick={savePerms}>
              {permSaving ? "Saving..." : "Save Permissions"}
            </button>

            <button style={S.btn} onClick={() => setPermList(PERMS.map((p) => p.code))}>
              Select all
            </button>

            <button style={S.btn} onClick={() => setPermList([])}>
              Clear
            </button>

            <button style={S.btn} onClick={() => setPermList([
              "hotel.view",
              "hotel.rooms",
              "hotel.guests",
              "hotel.checkin",
              "hotel.stays",
            ])}>
              Reception perms
            </button>

            <button style={S.btn} onClick={() => setPermList([
              "hotel.view",
              "hotel.rooms",
              "hotel.guests",
              "hotel.checkin",
              "hotel.stays",
              "hotel.supplies",
              "hotel.shifts",
              "hotel.reports",
            ])}>
              Hotel manager perms
            </button>
          </div>
        </div>
      )}

      {pwUser && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0 }}>
              Reset Password: {pwUser.full_name || pwUser.username} (#{pwUser.id})
            </h3>

            <button style={{ ...S.btn, marginLeft: "auto" }} onClick={() => setPwUser(null)}>
              Close
            </button>
          </div>

          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 220px", gap: 10 }}>
            <div>
              <div style={S.label}>New password (min 6 chars)</div>
              <input style={S.input} type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
            </div>

            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button style={S.btnP} disabled={pwSaving} onClick={savePassword}>
                {pwSaving ? "Saving..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}